import { isCopEnabled } from './cookieUtils'

export const extractAccessToken = (rawToken: string | null): string => {
  if (!rawToken) {
    return ''
  }

  const trimmedToken = rawToken.trim()

  if (!trimmedToken.startsWith('{')) {
    return trimmedToken
  }

  try {
    const parsed = JSON.parse(trimmedToken) as {
      access_token?: string
      accessToken?: string
      token?: string
    }

    return parsed.access_token || parsed.accessToken || parsed.token || ''
  } catch {
    return ''
  }
}

export const getSilentToken = (): string => extractAccessToken(localStorage.getItem('cop_access_token'))

const TOKEN_ACQUIRED_EVENT = 'cop:token-acquired'
const DEFAULT_TOKEN_WAIT_MS = 10000

/**
 * Resolves the COP silent token, waiting for the async acquisition flow to
 * complete on a cold first load (token acquired via hidden iframe after login).
 *
 * - Returns immediately with the cached token when one already exists.
 * - Otherwise waits for the one-shot `cop:token-acquired` event.
 * - Falls back after `timeoutMs` so callers never hang forever if the token is
 *   never acquired (e.g. iframe failure); the request then proceeds with
 *   whatever token is available and the server enforces authorization.
 */
export const waitForCopToken = (timeoutMs = DEFAULT_TOKEN_WAIT_MS): Promise<string> => {
  const existing = getSilentToken()
  if (existing) {
    return Promise.resolve(existing)
  }

  if (typeof document === 'undefined') {
    return Promise.resolve('')
  }

  return new Promise<string>(resolve => {
    let settled = false

    const finish = (): void => {
      if (settled) return
      settled = true
      document.removeEventListener(TOKEN_ACQUIRED_EVENT, onAcquired)
      if (timerId !== undefined) window.clearTimeout(timerId)
      resolve(getSilentToken())
    }

    const onAcquired = (): void => finish()

    const timerId: number | undefined =
      typeof window !== 'undefined' ? window.setTimeout(finish, timeoutMs) : undefined

    document.addEventListener(TOKEN_ACQUIRED_EVENT, onAcquired, { once: true })
  })
}

export const buildCopHeaders = (token?: string): Record<string, string> => {
  // Authoritative COP gate: every COP service (http.service and the raw-axios
  // services) builds its request headers here, so throwing when the user is not
  // a COP/PowerPortal member stops any COP API call at the single shared choke
  // point. Client-side UX guard only — server-side authz is the real boundary.
  if (!isCopEnabled()) {
    throw new Error('COP is not enabled for this user')
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Customer-Id': sessionStorage.getItem('cop:customer-id') || 'NOSLA',
    
    'x-user-selected-language-code': 'en',
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  return headers
}
